import { FormEvent, useState } from 'react';

type MaintenancePageProps = {
  onUnlock: (code: string) => boolean;
};

export default function MaintenancePage({ onUnlock }: MaintenancePageProps) {
  const [code, setCode] = useState('');
  const [invalidCode, setInvalidCode] = useState(false);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const isValid = onUnlock(code.trim());

    if (isValid) {
      return;
    }

    setCode('');
    setInvalidCode(true);
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <main className="relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div className="absolute inset-0" style={{
            backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, #B8FF3B 2px, #B8FF3B 4px)',
            backgroundSize: '100% 42px'
          }}></div>
        </div>

        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-[34rem] h-[34rem] rounded-full blur-3xl" style={{ backgroundColor: 'rgba(184,255,59,0.12)' }}></div>
          <div className="absolute bottom-0 left-0 w-80 h-80 rounded-full blur-3xl" style={{ backgroundColor: 'rgba(184,255,59,0.08)' }}></div>
          <div className="absolute top-1/3 right-0 w-96 h-96 rounded-full blur-3xl" style={{ backgroundColor: 'rgba(184,255,59,0.08)' }}></div>
        </div>

        <section className="relative min-h-screen flex items-center py-20">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 w-full">
            <div className="max-w-5xl mx-auto text-center">
              <div
                className="inline-flex items-center gap-3 px-5 py-3 rounded-full text-sm font-bold uppercase tracking-[0.24em] border mb-8"
                style={{ backgroundColor: 'rgba(184,255,59,0.1)', borderColor: 'rgba(184,255,59,0.35)', color: '#B8FF3B' }}
              >
                <i className="ri-tools-line text-lg"></i>
                Maintenance Mode
              </div>

              <div className="text-[4.5rem] sm:text-[6rem] lg:text-[8rem] font-black tracking-tight leading-none mb-6">
                Pauza na <span style={{ color: '#B8FF3B' }}>ďalší set</span>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mb-6">
                Web je práve v <span style={{ color: '#B8FF3B' }}>servisnom režime</span>
              </h1>

              <p className="text-xl lg:text-2xl text-gray-300 max-w-3xl mx-auto leading-relaxed mb-10">
                Ladíme detaily, aby stránka išla rovnako čisto ako dobrý tréning.
                Daj nám chvíľu a vrátime sa silnejší.
              </p>

              <div className="grid md:grid-cols-3 gap-6 mb-12">
                {[
                  {
                    icon: 'ri-flashlight-line',
                    title: 'Optimalizujeme výkon',
                    description: 'Doladíme rýchlosť, stabilitu a vizuálne detaily.'
                  },
                  {
                    icon: 'ri-shield-check-line',
                    title: 'Kontrolujeme kvalitu',
                    description: 'Prechádzame obsah, linky a posledné úpravy.'
                  },
                  {
                    icon: 'ri-timer-line',
                    title: 'Vraciame sa čoskoro',
                    description: 'Servisný režim je dočasný a po deployi sa vypne.'
                  }
                ].map((item) => (
                  <div
                    key={item.title}
                    className="bg-white/5 border border-white/10 rounded-2xl p-6 text-left"
                  >
                    <div
                      className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                      style={{ backgroundColor: 'rgba(184,255,59,0.12)', border: '1px solid rgba(184,255,59,0.3)' }}
                    >
                      <i className={`${item.icon} text-2xl`} style={{ color: '#B8FF3B' }}></i>
                    </div>
                    <h2 className="text-xl font-black mb-2">{item.title}</h2>
                    <p className="text-gray-400 leading-relaxed">{item.description}</p>
                  </div>
                ))}
              </div>

              <form
                onSubmit={handleSubmit}
                className="max-w-md mx-auto bg-white/5 border border-white/10 rounded-3xl p-6 sm:p-8 shadow-2xl"
              >
                <div className="flex gap-3">
                  <input
                    type="password"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    aria-label="Servisný kód"
                    placeholder="Kód"
                    value={code}
                    onChange={(event) => {
                      setCode(event.target.value);
                      if (invalidCode) {
                        setInvalidCode(false);
                      }
                    }}
                    className={`flex-1 min-w-0 rounded-2xl px-5 py-4 bg-[#111111] border text-white placeholder:text-gray-500 outline-none transition-colors ${
                      invalidCode ? 'border-red-500' : 'border-white/10 focus:border-[#B8FF3B]'
                    }`}
                  />
                  <button
                    type="submit"
                    className="shrink-0 px-6 py-4 font-bold rounded-2xl text-black transition-all duration-300 hover:scale-[1.01]"
                    style={{ backgroundColor: '#B8FF3B', boxShadow: '0 0 24px rgba(184,255,59,0.2)' }}
                  >
                    Vstúpiť
                  </button>
                </div>
              </form>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
